// services/storageFallback.js
//
// Some desktop browsers block firebasestorage.googleapis.com outright (ad/tracker blockers,
// antivirus web shields, DNS filters) — the request fails with net::ERR_BLOCKED_BY_CLIENT before
// it leaves the browser. Firestore and Cloud Functions still work, so the app loads its profiles
// but every cake image stays empty, and the Firebase SDK upload behind "create profile" retries
// silently for up to ten minutes (it looks stuck).
//
// This module routes exactly those two things through the app's own Cloud Functions host instead:
//   • images:  an <img> whose Firebase Storage URL fails is re-pointed at /storageImage once
//   • uploads: when the host is blocked (or the SDK upload stalls), /uploadCustomImage stores the
//              file in the same place and returns the same kind of download URL.
// Phones and browsers that can reach Firebase Storage never touch the fallback.

import { FN_BASE } from './fnBase';

const STORAGE_HOST = 'firebasestorage.googleapis.com';
const FALLBACK_ATTR = 'data-pcake-storage-fallback';

let blockedProbe = null;   // Promise<boolean>, resolved once per page load
let knownBlocked = false;  // set as soon as any direct request is seen failing

export const isFirebaseStorageUrl = (url) => typeof url === 'string' && url.includes(STORAGE_HOST);

/** The same image, served through the Cloud Functions host. */
export const proxiedImageUrl = (url) =>
  isFirebaseStorageUrl(url) ? `${FN_BASE}/storageImage?url=${encodeURIComponent(url)}` : url;

/**
 * Whether this browser can reach Firebase Storage. A blocked host fails in a few milliseconds
 * with a TypeError; a reachable one answers (any HTTP status, usually 400/403 for this request).
 */
export const isStorageBlocked = () => {
  if (knownBlocked) return Promise.resolve(true);
  if (!blockedProbe) {
    blockedProbe = (async () => {
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = setTimeout(() => ctrl?.abort(), 6000);
      try {
        await fetch(`https://${STORAGE_HOST}/v0/b/pcakedatastore.appspot.com/o?maxResults=1`, {
          method: 'GET', cache: 'no-store', signal: ctrl?.signal,
        });
        return false;
      } catch {
        knownBlocked = true;
        return true;
      } finally {
        clearTimeout(timer);
      }
    })();
  }
  return blockedProbe;
};

/**
 * Re-points failed Firebase Storage images at the proxy. Capture phase, because `error` events
 * on <img> do not bubble. Each image is retried once, so a real 404 does not loop.
 */
export const installImageFallback = () => {
  if (typeof document === 'undefined' || window.__pcakeImageFallback) return;
  window.__pcakeImageFallback = true;
  document.addEventListener('error', (event) => {
    const el = event.target;
    if (!el || el.tagName !== 'IMG') return;
    const src = el.getAttribute('src') || '';
    if (!isFirebaseStorageUrl(src) || el.hasAttribute(FALLBACK_ATTR)) return;
    knownBlocked = true;
    el.setAttribute(FALLBACK_ATTR, '1');
    el.setAttribute('src', proxiedImageUrl(src));
  }, true);
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(reader.error || new Error('Could not read the image'));
  reader.readAsDataURL(blob);
});

/** Upload through the Cloud Function. Returns the Firebase download URL. */
export const uploadViaFunction = async (user, blob, fileName) => {
  if (!user) throw new Error('No authenticated user found.');
  let payloadBlob = blob;
  // The function accepts up to 7 MB; shrink anything bigger first.
  if (blob.size > 6.5 * 1024 * 1024 && blob.type.startsWith('image/')) {
    const { default: imageCompression } = await import('browser-image-compression');
    payloadBlob = await imageCompression(blob, { maxSizeMB: 4, maxWidthOrHeight: 2400, useWebWorker: true });
  }
  const idToken = await user.getIdToken();
  const response = await fetch(`${FN_BASE}/uploadCustomImage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({
      content: await blobToBase64(payloadBlob),
      contentType: payloadBlob.type || 'image/jpeg',
      fileName,
    }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.downloadURL) {
    throw new Error(result?.error?.message || `Upload failed (${response.status})`);
  }
  return result.downloadURL;
};

/** Resolves with the promise's value, or rejects after `ms`. */
export const withTimeout = (promise, ms, message) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(Object.assign(new Error(message), { code: 'pcake/timeout' })), ms);
  promise.then(
    (v) => { clearTimeout(timer); resolve(v); },
    (e) => { clearTimeout(timer); reject(e); },
  );
});
